/**
 * Poll while a job runs: call `poll` at once, then again `intervalMs` after
 * each answer, for as long as `active` is true. Stops on unmount and when
 * `active` turns false.
 *
 * - One request at a time: the next poll waits for the last one to settle. A
 *   stuck instance holds a request for up to 120 s; polls fired every second
 *   regardless piled up behind it and answered out of order. A poll still
 *   open after `POLL_TIMEOUT_MS` is aborted and counts as failed.
 * - A failed poll — `poll` resolving `'retry'` or throwing — doubles the wait,
 *   up to `MAX_POLL_INTERVAL_MS`; the next answer goes back to `intervalMs`.
 * - While the sign-in has expired (`reportSessionExpired`, after a 401)
 *   nothing is polled. The page keeps its job and the job's id, and polling
 *   resumes once the session is back.
 * - `signal` is aborted when this round of polling ends — the job finished or
 *   was stopped, the page unmounted — or the request timed out. An answer
 *   arriving after that must not be applied: `poll` checks `signal.aborted`
 *   after each `await`, so a late "running" cannot follow a "completed".
 * - The first call runs from a timer, not in the effect body: `poll` sets
 *   state, and setting state synchronously inside an effect re-renders in a
 *   cascade (`react-hooks/set-state-in-effect`).
 * - The latest `poll` is always the one called, so it may close over fresh
 *   state without restarting the polling.
 * - A page kept mounted in the background keeps polling — a running job keeps
 *   its progress while the editor works in another tool.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react'

import { isSessionExpired, reportSessionExpired, subscribeSession } from '../utils/session'

/** What one poll came to: `'retry'` when it failed, nothing when it was answered. */
export type PollOutcome = 'retry' | void

/** The longest wait between two polls, reached after repeated failures. */
export const MAX_POLL_INTERVAL_MS = 15_000

/** A status request still open after this long is given up. */
export const POLL_TIMEOUT_MS = 20_000

/**
 * Consecutive 404s after which a job counts as gone. One 404 proves nothing:
 * it also comes from an instance that has not read the job's snapshot yet,
 * and the first write to S3 can fail twice before one lands.
 */
export const MAX_MISSED_POLLS = 20

export function usePolling(
    poll: (signal: AbortSignal) => PollOutcome | Promise<PollOutcome>,
    intervalMs: number,
    active: boolean,
): void {
    const pollRef = useRef(poll)
    useEffect(() => {
        pollRef.current = poll
    })
    const expired = useSyncExternalStore(subscribeSession, isSessionExpired, isSessionExpired)
    const polling = active && !expired

    useEffect(() => {
        if (!polling) return
        let ended = false
        let request: AbortController | null = null
        let wait = intervalMs
        let timer: ReturnType<typeof setTimeout>
        const tick = async () => {
            const controller = new AbortController()
            request = controller
            const timeout = setTimeout(() => controller.abort(), POLL_TIMEOUT_MS)
            let outcome: PollOutcome
            try {
                outcome = await pollRef.current(controller.signal)
            } catch (err) {
                console.error('Status poll failed', err)
                outcome = 'retry'
            } finally {
                clearTimeout(timeout)
            }
            if (ended) return
            const failed = outcome === 'retry' || controller.signal.aborted
            wait = failed ? Math.min(wait * 2, MAX_POLL_INTERVAL_MS) : intervalMs
            timer = setTimeout(tick, wait)
        }
        timer = setTimeout(tick, 0)
        return () => {
            ended = true
            clearTimeout(timer)
            request?.abort()
        }
    }, [polling, intervalMs])
}

/** A status answer: the job's snapshot, or the HTTP status that came instead (0: no answer at all). */
export type StatusAnswer<T> = { ok: true; data: T } | { ok: false; status: number }

/**
 * GET a job's status for a poll. A 401 is reported to the shell, which pauses
 * every poll until the sign-in is back.
 */
export async function fetchStatus<T>(url: string, headers: HeadersInit, signal?: AbortSignal): Promise<StatusAnswer<T>> {
    try {
        const response = await fetch(url, { headers, signal })
        if (response.status === 401) reportSessionExpired()
        if (!response.ok) return { ok: false, status: response.status }
        return { ok: true, data: (await response.json()) as T }
    } catch {
        return { ok: false, status: 0 }
    }
}

/**
 * How a poll goes on after a failed status request: a 404 — an instance that
 * has not read the job yet — is asked again at the usual pace; anything else
 * (no answer, a 5xx, a 401 until the session is back) waits longer each time.
 */
export function afterFailure(answer: { status: number }): PollOutcome {
    return answer.status === 404 ? undefined : 'retry'
}
